import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import { REPLY_TO_INQUIRY } from '../../../graphql/mutations';
import { Spinner } from '../../common/Spinner';

const MAX_LENGTH = 5000;

interface InlineReplyFormProps {
  inquiryId: string;
  /**
   * Called after the reply mutation returns `success: true`. The parent
   * (AdminInquiryListPage) uses this to reset pagination, refetch the list
   * + counts, and show the success toast.
   */
  onReplySuccess: () => void;
}

interface ReplyToInquiryResponse {
  replyToInquiry: {
    success: boolean;
    message?: string | null;
    error?: string | null;
    data?: {
      inquiryId: string;
      answer?: string | null;
      answeredAt?: string | null;
      answeredBy?: string | null;
      status?: string | null;
      updatedAt?: string | null;
    } | null;
  };
}

/**
 * Inline reply form rendered inside an expanded inquiry row. Submits a reply
 * via `replyToInquiry` and delegates success handling to the parent page.
 */
const InlineReplyForm: React.FC<InlineReplyFormProps> = ({ inquiryId, onReplySuccess }) => {
  const { t } = useTranslation('admin');
  const [value, setValue] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  const [replyToInquiry, { loading }] = useMutation<ReplyToInquiryResponse>(
    gql`${REPLY_TO_INQUIRY}`,
  );

  const trimmedLength = value.trim().length;
  const tooLong = value.length > MAX_LENGTH;
  const submitDisabled = trimmedLength === 0 || tooLong || loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitDisabled) return;
    setInlineError(null);
    try {
      const res = await replyToInquiry({
        variables: { input: { inquiryId, answer: value } },
      });
      const payload = res.data?.replyToInquiry;
      if (!payload || !payload.success) {
        // Keep the textarea contents intact per Requirement 7.7.
        setInlineError(
          payload?.error || payload?.message || (t('inquiries.toast.failure') as string),
        );
        return;
      }
      // Reset local state before bubbling up; parent will collapse/refetch.
      setValue('');
      onReplySuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setInlineError(msg || (t('inquiries.toast.failure') as string));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-2">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t('inquiries.replyForm.placeholder') as string}
        rows={5}
        className={`block w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 ${
          tooLong
            ? 'border-red-400 focus:ring-red-500'
            : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
        }`}
        disabled={loading}
      />

      <div className="flex items-center justify-between">
        <span
          className={`text-xs ${tooLong ? 'text-red-600 font-medium' : 'text-gray-400'}`}
        >
          {t('inquiries.replyForm.charCounter', { current: value.length, max: MAX_LENGTH })}
        </span>

        <button
          type="submit"
          disabled={submitDisabled}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading && <Spinner size="small" className="!text-white" />}
          {loading
            ? t('inquiries.replyForm.submitting')
            : t('inquiries.replyForm.submit')}
        </button>
      </div>

      {inlineError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {inlineError}
        </div>
      )}
    </form>
  );
};

export default InlineReplyForm;
